import { resolveAnchor, type AnchorSpec } from '../engine/anchors';

export function findVisible(selector: string): HTMLElement | null {
  return resolveAnchor({ selector })?.element ?? null;
}

export function clickWhenReady(spec: AnchorSpec, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      const hit = resolveAnchor(spec)?.element;
      if (hit) {
        hit.click();
        return resolve(true);
      }
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

function pointerish(type: string, init: MouseEventInit & { pointerId?: number }): Event {
  const full = {
    bubbles: true,
    cancelable: true,
    composed: true,
    button: 0,
    ...init,
  };
  if (typeof PointerEvent === 'function') {
    return new PointerEvent(type, { ...full, pointerId: init.pointerId ?? 1, pointerType: 'mouse', isPrimary: true });
  }
  const event = new MouseEvent(type, full);
  Object.defineProperty(event, 'pointerId', { get: () => init.pointerId ?? 1 });
  Object.defineProperty(event, 'pointerType', { get: () => 'mouse' });
  return event;
}

function centreOf(el: HTMLElement): { clientX: number; clientY: number } {
  const rect = el.getBoundingClientRect();
  return { clientX: Math.round(rect.left + rect.width / 2), clientY: Math.round(rect.top + rect.height / 2) };
}

export function hoverElement(el: HTMLElement): void {
  const at = centreOf(el);
  el.dispatchEvent(pointerish('pointerover', at));
  el.dispatchEvent(pointerish('pointerenter', { ...at, bubbles: false }));
  el.dispatchEvent(pointerish('pointermove', at));
  el.dispatchEvent(new MouseEvent('mouseover', { bubbles: true, cancelable: true, ...at }));
  el.dispatchEvent(new MouseEvent('mousemove', { bubbles: true, cancelable: true, ...at }));
}

export function pressElement(el: HTMLElement): void {
  const at = centreOf(el);
  el.dispatchEvent(pointerish('pointerdown', { ...at, buttons: 1 }));
  el.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, buttons: 1, ...at }));
  el.dispatchEvent(pointerish('pointerup', { ...at, buttons: 0 }));
  el.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, ...at }));
  el.click();
}

export function pressWhenReady(spec: AnchorSpec, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      const hit = resolveAnchor(spec)?.element;
      if (hit) {
        hoverElement(hit);
        pressElement(hit);
        return resolve(true);
      }
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

export function waitForAnchor(spec: AnchorSpec, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      if (resolveAnchor(spec)) return resolve(true);
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function waitUntil(predicate: () => boolean, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      if (predicate()) return resolve(true);
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

type Field = HTMLInputElement | HTMLTextAreaElement;

export function findField(selector: string): Field | null {
  const el = findVisible(selector);
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el;
  return null;
}

function nativeValueSetter(el: Field): ((value: string) => void) | null {
  const proto =
    el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  return setter ? (value: string) => setter.call(el, value) : null;
}

export interface FillOptions {
  onlyIfEmpty?: boolean;
}

export function fillField(
  selector: string,
  value: string,
  options: FillOptions = {},
): boolean {
  const el = findField(selector);
  return el ? fillElement(el, value, options) : false;
}

export function fillElement(el: Field, value: string, { onlyIfEmpty = true }: FillOptions = {}): boolean {
  if (el.disabled || el.readOnly) return false;
  if (onlyIfEmpty && el.value.trim() !== '') return false;

  const setValue = nativeValueSetter(el);
  if (!setValue) return false;

  setValue(value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
  el.focus();
  el.blur();
  return true;
}

export function isFilled(selector: string): boolean {
  return (findField(selector)?.value ?? '').trim() !== '';
}

export function chooseFirstOption(
  triggerSpec: AnchorSpec,
  { timeoutMs = 3000, optionSelector = '[role="option"]' } = {},
): Promise<boolean> {
  const trigger = resolveAnchor(triggerSpec)?.element;
  if (!trigger) return Promise.resolve(false);

  if (trigger.getAttribute('data-state') !== 'open') trigger.click();

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      const option = Array.from(document.querySelectorAll<HTMLElement>(optionSelector)).find(
        (el) => el.getAttribute('aria-disabled') !== 'true' && el.getAttribute('data-disabled') === null,
      );
      if (option) {
        option.click();
        return resolve(true);
      }
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

export function isChosen(selector: string, placeholder: string): boolean {
  const trigger = findVisible(selector);
  if (!trigger) return false;
  const text = (trigger.textContent ?? '').trim();
  return text !== '' && text !== placeholder.trim();
}

export function isChecked(selector: string): boolean {
  const el = findVisible(selector);
  if (!el) return false;
  const aria = el.getAttribute('aria-checked');
  if (aria !== null) return aria === 'true';
  return el instanceof HTMLInputElement ? el.checked : false;
}

export function ensureChecked(selector: string): boolean {
  if (isChecked(selector)) return true;
  const el = findVisible(selector);
  if (!el || (el as HTMLButtonElement).disabled) return false;
  el.click();
  return isChecked(selector);
}

export function isSelected(selector: string): boolean {
  const trigger = findVisible(selector);
  if (!trigger) return false;
  if (trigger.querySelector('[data-placeholder]')) return false;
  return (trigger.textContent ?? '').trim() !== '';
}
